import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { login as apiLogin, getJson, mutateJson } from '../api/client';

export interface SessionUser {
  id: number;
  papel: string;
  pessoaId: number | null;
  /** Módulos ativos do tenant (gating, #126); vazio esconde todo módulo opcional. */
  modulos: readonly string[];
  /** Condomínio suspenso (#127): a API recusa escrita do tenant; a UI avisa e não oferece. */
  suspenso: boolean;
}

interface AuthState {
  loading: boolean;
  user: SessionUser | null;
}

interface AuthContextValue extends AuthState {
  /** Tenta autenticar; retorna o status HTTP (200 ok, 401 inválido, 429 limite). */
  signIn: (email: string, password: string) => Promise<number>;
  signOut: () => Promise<void>;
  /** Recarrega a sessão a partir de /api/me. */
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ loading: true, user: null });

  const refresh = useCallback(async () => {
    const data = await getJson<{
      user: Omit<SessionUser, 'modulos' | 'suspenso'> & {
        modulos?: unknown;
        tenantStatus?: unknown;
      };
    }>('/api/me');
    const user = data?.user;
    // Sem lista válida de módulos, falha fechada: só telas core aparecem.
    const modulos = Array.isArray(user?.modulos)
      ? user.modulos.filter((m): m is string => typeof m === 'string')
      : [];
    setState({
      loading: false,
      user: user
        ? {
            id: user.id,
            papel: user.papel,
            pessoaId: user.pessoaId,
            modulos,
            suspenso: user.tenantStatus === 'suspenso',
          }
        : null,
    });
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const signIn = useCallback(
    async (email: string, password: string) => {
      const res = await apiLogin(email, password);
      if (res.ok) await refresh();
      return res.status;
    },
    [refresh],
  );

  const signOut = useCallback(async () => {
    await mutateJson('/api/logout', {});
    setState({ loading: false, user: null });
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ ...state, signIn, signOut, refresh }),
    [state, signIn, signOut, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth deve ser usado dentro de <AuthProvider>');
  return ctx;
}
